import { get } from "lodash";
import * as CryptoJS from "crypto-js";
import * as JsChardet from "jschardet";
import jwtDecode from "jwt-decode";
import { thor } from "../typings/thor";
import { Enums } from "../thor.enums";
import { SanitizeAndExtractHtmlResult } from "../utils/sanitize-and-extract-html";
import { LOCALE } from "../thor.const";

class UtilsService {

    public static $inject: string[] = [
        "$log",
        "$q",
        "$translate"
    ];

    constructor(
        private $log: ng.ILogService,
        private $q: ng.IQService,
        private $translate: ng.translate.ITranslateService
    ) {}

    /*
    * @param dataURI base64 of picture
    * @param jpegOnly limit to jpeg when true
    * @returns {Blob}
    */
    public dataURItoBlob(dataURI, jpegOnly = true) {
        const [meta, encodedData] = dataURI.split(",");
        const [mimeType, encoding] = meta.replace("data:", "").split(";");

        if (jpegOnly && ! /^(image\/jpe?g)$/i.test(mimeType)) {
            this.$log.error(`mimeType (${mimeType}) is not allowed`);
            return null;
        }

        if (encoding !== "base64") {
            this.$log.error(`encoding (${encoding}) is not allowed`);
            return null;
        }

        const decodedData = atob(encodedData);

        // write the bytes of the string to a typed array
        const bytes = new Uint8Array(decodedData.length);
        for (let i = 0; i < decodedData.length; i++) {
            bytes[i] = decodedData.charCodeAt(i);
        }

        const wordArray = CryptoJS.lib.WordArray.create(bytes);
        const hash = CryptoJS.MD5(wordArray).toString();

        return {
            blob: new Blob([bytes], { type: mimeType }),
            md5: hash
        };
    }

    public decodeToken (token) {
        try {
            if (token) {
                return jwtDecode(token);
            } else {
                return null;
            }
        } catch (error) {
            this.$log.error("Error while decoding access token", error, token);
            return null;
        }
    }

    public detectStringEncoding(str) {
        const data = JsChardet.detect(str);

        if (data.confidence > 0) {
            return data.encoding;
        }

        return false;
    }

    public findItemByOrderNumber(orderNumber: thor.ItemMetaOrderNumber, items: Array<thor.ItemGet>) {
        // @ts-ignore
        return <thor.ItemGet> items.find(item => {
            let isFound: boolean;
            const itemOrderNumber = item.meta?.order_number || { primary: null };

            isFound = itemOrderNumber.primary && itemOrderNumber.primary === orderNumber.primary;

            if (isFound) {
                if (orderNumber.secondary) {
                    isFound = itemOrderNumber.secondary === orderNumber.secondary;
                } else if (itemOrderNumber.secondary) {
                    isFound = false;
                }
            }

            return isFound;
        });
    }

    public formatPhoneNumber(phone: thor.UserPhone, { v2Formatted } = { v2Formatted: false }): string {
        if (!phone) {
            return "";
        }
        if (v2Formatted) {
            return `(${phone?.prefix || ""})${parseInt(phone?.number, 10) || ""}`;
        }
        const prefix = (get(phone, "prefix", "") || "").replace(/^(\+)?/i, "");
        const num = (get(phone, "number", "") || "").replace(/^(0)?/i, "");
        return `${prefix}${num}`;
    }

    public getContentRange(currentApiCallOffset: number, apiLimit: number) {
        const from = currentApiCallOffset * apiLimit + 1;
        const to = (currentApiCallOffset + 1) * apiLimit;

        return `items=${from}-${to}`;
    }

    public getFileExtension(filename) {
        if (!filename) {
            return "";
        }

        let extension = filename.split(".").pop();

        if (typeof extension !== "string") {
            return "";
        }

        extension = extension.toLowerCase();
        extension = extension.replace("jpeg", "jpg");

        return extension;
    }

    public getCurrentLang(): Enums.Locale {
        return <Enums.Locale>this.$translate.use() || LOCALE;
    }

    public getOrderNumberNormalized(item) {
        const primary = get(item, "meta.order_number.primary");
        const secondary = get(item, "meta.order_number.secondary");

        if (!primary) {
          return "";
        }

        return `${primary}${secondary ? `-${secondary}` : ""}`.replace(/\s/g, "");
    }

    public getOrderNumber(item: thor.ItemGet): { primary: number; secondary: string; id: number } {
        return {
            primary: parseInt(get(item, "meta.order_number.primary", 0), 10),
            secondary: get(item, "meta.order_number.secondary", "") || "",
            id: item.id
        };
    }

    public areOrdersNumbersEquals(aItem: thor.ItemGet, bItem: thor.ItemGet): boolean {
        return this.getOrderNumberNormalized(aItem) === this.getOrderNumberNormalized(bItem);
    }

    public isChrome() {
        const isChromium = window.chrome,
            winNav = window.navigator,
            vendorName = winNav.vendor,
            isOpera = winNav.userAgent.indexOf("OPR") > -1,
            isIEedge = winNav.userAgent.indexOf("Edge") > -1;

        return (
            isChromium !== null &&
            typeof isChromium !== "undefined" &&
            vendorName === "Google Inc." &&
            isOpera === false &&
            isIEedge === false
        );
    }

    public isMobile() {
        return !!navigator.userAgent.match(/(iPad)|(iPhone)|(iPod)|(android)|(webOS)/i);
    }

    public isShippingFilled(shipping: thor.Shipping) {
        return Boolean(shipping?.withdrawal_conditions);
    }

    /*
     * check shipping (withdrawal conditions) is required
     * The organization must complete this information in myAccount, otherwise:
     * - Create a new sale is not possible
     * - Edit and publish an already created sale is possible
     * @todo remove soon
     */
    public isShippingRequired(organization: thor.Organization, sale: thor.SaleGet): boolean {
        if (
            !this.isShippingFilled(organization.shipping) &&
            [Enums.SaleStatus.Created, Enums.SaleStatus.Published, Enums.SaleStatus.Edited].includes(sale.status)
        ) {
            return false;
        }
        return true;
    }

    public range(begin: number, end: number, interval: number = 1): number[] {
        const range = [];

        if (interval >= 1) {
            for (let i = begin; i <= end; i = i + interval) {
                range.push(i);
            }
        }

        return range;
    }

    public readFileAsString(file) {
        const deferred = this.$q.defer();
        const reader = new FileReader();

        reader.readAsBinaryString(file);
        reader.onload = event => {
            const str = (<any>event).target.result;
            const encoding = this.detectStringEncoding(str);

            if (encoding !== false) {
                const readerEncoded = new FileReader();

                readerEncoded.readAsText(file, encoding);
                readerEncoded.onload = eventEncoded => {
                    return deferred.resolve((<any>eventEncoded).target.result);
                };
            } else {
                this.$log.error("Failed to detect file encoding");
                return deferred.reject(
                    this.$translate.instant("erreur_lors_de_l_import_du_fichier_echec_de_la_detection_de_l_encodage")
                );
            }
        };

        reader.onerror = error => {
            return deferred.reject(error);
        };

        return deferred.promise;
    }

    public slugify(text: string) {
        text = this.stringNormalize(text, true);

        return text.toString()
            .replace(/\s+/g, "-")           // Replace spaces with -
            .replace(/&/g, "-et-")         // Replace & with 'and'
            .replace(/[^\w\-]+/g, "")       // Remove all non-word chars
            .replace(/\-\-+/g, "-")         // Replace multiple - with single -
            .replace(/^-+/, "")             // Trim - from start of text
            .replace(/-+$/, "");             // Trim - from end of text
    }

    /**
     * Lowercase string, replace accents and white spaces
     * @param {string} str String to be normalize
     * @param {boolean} slugMode On slugMode, normalize will be less strict and no longer remove
     * white spaces
     * @returns {string} Normalized string
     */
    public stringNormalize(str: string, slugMode: boolean = false) {
        if (!str) {
            return "";
        }

        str = str.toLowerCase();

        if (!slugMode) {
            str = str.replace(/\s/g, "");
            str = str.replace(/\W/g, "");
        }

        str = str.replace(/[àáâãäå]/g, "a");
        str = str.replace(/æ/g, "ae");
        str = str.replace(/ç/g, "c");
        str = str.replace(/[èéêë]/g, "e");
        str = str.replace(/[ìíîï]/g, "i");
        str = str.replace(/ñ/g, "n");
        str = str.replace(/[òóôõö]/g, "o");
        str = str.replace(/œ/g, "oe");
        str = str.replace(/€/g, "eur");
        str = str.replace(/£/g, "gbp");
        str = str.replace(/\$/g, "usd");
        str = str.replace(/ß/g, "ss");
        str = str.replace(/[ùúûü]/g, "u");
        str = str.replace(/[ýÿ]/g, "y");

        return str;
    }

    public orderNumberSort(
        a: thor.SortOrderNumberParameter,
        b: thor.SortOrderNumberParameter,
        isReverse: boolean = false
    ) {
        let res = 0;

        if (a.primary < b.primary) {
            res = -1;
        } else if (a.primary > b.primary) {
            res = 1;
        } else if (a.primary === b.primary) {
            if (a.secondary < b.secondary) {
                res = -1;
            } else if (a.secondary > b.secondary) {
                res = 1;
            } else if (a.secondary === b.secondary) {
                res = a.id < b.id ? -1 : 1;
            }
        }

        // make null values come last when sorting ascending
        if ((a.primary === 0 || b.primary === 0) && a.primary !== b.primary) {
            res *= -1;
        }

        if (isReverse) {
            res *= -1;
        }

        return res;
    }

    public stringSort(a: thor.ItemGet, b: thor.ItemGet, field: string | string[], isReverse: boolean) {
        const getValue = o =>
            this.stringNormalize(
                typeof field === "string" ? get(o, field, "") : field.map(f => get(o, f, "")).toString()
            );

        const aValue = getValue(a);
        const bValue = getValue(b);
        let res = 0;

        if (aValue < bValue) {
            res = -1;
        } else if (aValue > bValue) {
            res = 1;
        } else if (aValue === bValue) {
            res = a.id < b.id ? -1 : 1;
        }

        // make null values come last when sorting ascending
        if ((aValue === "" || bValue === "") && aValue !== bValue) {
            res *= -1;
        }

        if (isReverse) {
            res *= -1;
        }

        return res;
    }

    public numericSort(
        a: thor.ItemGet,
        b: thor.ItemGet,
        field: string,
        isReverse: boolean = false
    ) {
        const aValue = get(a, field, 0);
        const bValue = get(b, field, 0);
        let res = 0;

        if (aValue < bValue) {
            res = -1;
        } else if (aValue > bValue) {
            res = 1;
        } else if (aValue === bValue) {
            res = a.id < b.id ? -1 : 1;
        }

        // make null values come last when sorting ascending
        if ((aValue === 0 || bValue === 0) && aValue !== bValue) {
            res *= -1;
        }

        if (!(aValue === 0 || bValue === 0) && isReverse) {
            res *= -1;
        }

        return res;
    }

    public videoEmbedObjToString(embed: SanitizeAndExtractHtmlResult): string {
        if (embed.type !== "iframe") {
            return;
        }
        const attrs = Object.entries(embed.attrs)
            .reduce((acc, [k, v]) => [...acc, `${v ? `${k}="${v}"` : k}`], [])
            .join(" ");
        return `<${embed.type} ${attrs}></${embed.type}>`;
    }
}

angular
    .module("thor.core.utils", [])
    .service("utilsService", UtilsService);
